import { useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Delete, Edit, Send } from "@mui/icons-material";
import { formatDistanceToNow } from "date-fns";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../app/hooks";
import {
  addWallComment,
  deleteWallComment,
  editWallComment,
} from "../wallSlices";
import { WallComment, WallPost } from "../types/wallTypes";

type Props = {
  post: WallPost;
  currentUserId?: string;
  canModerate: boolean;
};

export default function CommentList({
  post,
  currentUserId,
  canModerate,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [text, setText] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");

  const submit = async () => {
    if (!text.trim()) return;
    await dispatch(addWallComment({ postId: post._id!, message: text.trim() }));
    setText("");
  };

  const saveEdit = async (commentId: string) => {
    if (!editText.trim()) return;
    await dispatch(
      editWallComment({ postId: post._id!, commentId, message: editText.trim() }),
    );
    setEditingId(null);
  };

  const canEdit = (c: WallComment) => c.author?._id === currentUserId;
  const canDelete = (c: WallComment) =>
    c.author?._id === currentUserId || canModerate;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack spacing={1.5}>
        {(post.comments ?? []).map((c) => (
          <Stack key={c._id} direction="row" spacing={1} alignItems="flex-start">
            <Avatar src={c.author?.avatar} sx={{ width: 28, height: 28 }}>
              {c.author?.name?.[0]}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {c.author?.name}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ·{" "}
                  {c.createdAt &&
                    formatDistanceToNow(new Date(c.createdAt), {
                      addSuffix: true,
                    })}
                  {c.editedAt ? ` · ${t("edited")}` : ""}
                </Typography>
              </Typography>
              {editingId === c._id ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    fullWidth
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <IconButton size="small" onClick={() => saveEdit(c._id!)}>
                    <Send fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Typography variant="body2">{c.message}</Typography>
              )}
            </Box>
            {canEdit(c) && editingId !== c._id && (
              <IconButton
                size="small"
                onClick={() => {
                  setEditingId(c._id!);
                  setEditText(c.message);
                }}
              >
                <Edit fontSize="small" />
              </IconButton>
            )}
            {canDelete(c) && (
              <IconButton
                size="small"
                onClick={() =>
                  dispatch(
                    deleteWallComment({ postId: post._id!, commentId: c._id! }),
                  )
                }
              >
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <TextField
          size="small"
          fullWidth
          placeholder={t("addComment")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        <IconButton color="primary" onClick={submit} disabled={!text.trim()}>
          <Send />
        </IconButton>
      </Stack>
    </Box>
  );
}
